import React, { useState } from "react";

function useParentRegistration({ value, setValue }) {
  const [infoClicked, setInfoClicked] = useState(false);
  const [fullAddressData, setFullAddressData] = useState({});
  const [addressData, setAddressData] = useState({});
  const [addressLookupFailed, setAddressLookupFailed] = useState(false);
  const [addressLookupAttempted, setAddressLookupAttempted] = useState(false);
  const googlePlacesApiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

  const getAddressLookup = async () => {
    const lookupText = (value.addressLineOne || "").trim();
    if (!lookupText) {
      setAddressData({});
      setAddressLookupFailed(false);
      return;
    }

    setAddressLookupAttempted(true);

    if (!googlePlacesApiKey) {
      setAddressData({});
      setAddressLookupFailed(true);
      return;
    }

    try {
      const response = await fetch(
        "https://places.googleapis.com/v1/places:autocomplete",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": googlePlacesApiKey,
          },
          body: JSON.stringify({
            input: lookupText,
            languageCode: "en",
            includedRegionCodes: ["gb"],
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch");
      }
      const data = await response.json();

      const suggestions = (data?.suggestions || [])
        .map((entry) => entry?.placePrediction)
        .filter(Boolean)
        .map((placePrediction) => ({
          id: placePrediction.placeId,
          address: placePrediction?.text?.text || "",
        }))
        .filter((suggestion) => suggestion.id && suggestion.address);

      if (suggestions.length > 0) {
        setAddressData({ suggestions });
        setAddressLookupFailed(false);
      } else {
        setAddressData({});
        setAddressLookupFailed(true);
      }
    } catch (error) {
      setAddressData({});
      setAddressLookupFailed(true);
    }
  };

  const handleInfoClicked = () => {
    setInfoClicked((prevState) => !prevState);
  };

  const getFullAddressLookup = async (addressId) => {
    setAddressLookupAttempted(true);

    if (!googlePlacesApiKey || !addressId) {
      setFullAddressData({});
      setAddressLookupFailed(true);
      return;
    }

    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${encodeURIComponent(addressId)}?languageCode=en`,
        {
          headers: {
            "X-Goog-Api-Key": googlePlacesApiKey,
            "X-Goog-FieldMask": "addressComponents,formattedAddress",
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch");
      }
      const data = await response.json();

      const components = Array.isArray(data?.addressComponents)
        ? data.addressComponents
        : [];

      const getComponent = (...types) =>
        components.find((component) =>
          (component.types || []).some((type) => types.includes(type))
        );

      const streetNumber =
        getComponent("street_number")?.longText ||
        getComponent("premise")?.longText ||
        "";
      const route = getComponent("route")?.longText || "";
      const subpremise = getComponent("subpremise")?.longText || "";
      const locality =
        getComponent("postal_town")?.longText ||
        getComponent("locality")?.longText ||
        getComponent("administrative_area_level_2")?.longText ||
        "";
      const postcode =
        getComponent("postal_code")?.longText ||
        getComponent("postal_code")?.shortText ||
        "";

      let line1 = [streetNumber, route].filter(Boolean).join(" ").trim();
      if (!line1 && data?.formattedAddress) {
        line1 = data.formattedAddress.split(",")[0]?.trim() || "";
      }

      if (line1 || locality || postcode) {
        setFullAddressData({
          line_1: line1,
          line_2: subpremise,
          town_or_city: locality,
          postcode,
        });
        setAddressLookupFailed(false);
      } else {
        setFullAddressData({});
        setAddressLookupFailed(true);
      }
    } catch (error) {
      setFullAddressData({});
      setAddressLookupFailed(true);
    }
  };

  const handleChange = (addressId) => {
    if (addressId) {
      setValue((prevValue) => ({
        ...prevValue,
        addressId: addressId,
      }));
    }
  };

  return {
    getAddressLookup,
    handleInfoClicked,
    getFullAddressLookup,
    handleChange,
    infoClicked,
    setInfoClicked,
    fullAddressData,
    setFullAddressData,
    addressData,
    setAddressData,
    addressLookupFailed,
    addressLookupAttempted,
  };
}
export default useParentRegistration;
